import React from 'react'
// @src imports
import {
  useAccount,
  useActions,
  useFlagsWithUseQueries,
  useSelector,
} from 'src/hooks'
import { ProfileAvatar, Transition } from 'src/chrome'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'

import { Div, Flex } from '@sendoutcards/quantum-design-ui'
import { TabBar } from 'src/design_system/components/TabBar/TabBar'
import { IconType } from 'src/design_system/atoms/icons/types'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'
import { ShareSettingsRoute } from 'src/dashboard/routes/ShareSettingsRoute'
import { DashboardSettingsRoute } from 'src/dashboard/routes/Settings'
import {
  useNotificationCount,
  useReceivedPendingMembershipInvites,
} from 'src/react_query'
import { MyGiftsRoute } from 'src/dashboard/routes/MyGiftsRoute'
import { MembershipRewards } from 'src/dashboard/components/MembershipRewards/MembershipRewards'

const styles = {
  dashboardHeader: {
    position: 'static' as const,
    width: '100%',
    height: 'auto',
    backgroundColor: '#F5F6F7',
    overflow: 'visible',
    paddingRight: '2rem',
    paddingLeft: '1rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    display: 'flex',
    flexDlow: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1000,
    '@media (max-width: 815px)': {
      padding: 0,
      justifyContent: 'center',
      columnGap: '16px',
    },
    '@media (max-width: 500px)': {
      paddingLeft: 0,
      '> div:last-child': {
        display: 'none',
      },
    },
  },
  headerSubtitle: {
    display: 'flex',
    alignItems: 'baseline',
    '@media (max-width: 1550px)': {
      flexDirection: 'column' as const,
    },
  },
  flexRowWrap: {
    display: 'flex',
    flexFlow: 'row wrap',
    width: '100%',
    justifyContent: 'center',
    overflow: 'visible',
    '@media (max-width: 500px)': {
      justifyContent: 'start',
      paddingLeft: '8px',
    },
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    '@media (max-width: 670px)': {
      alignItems: 'flex-start',
      paddingLeft: 20,
    },
    '@media (max-width: 500px)': {
      alignItems: 'center',
      paddingLeft: 0,
    },
  },
}

type DashboardHeaderProps = {
  routePath: string
  onOpenAccount: () => void
  toggleReminders: () => void
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onOpenAccount,
  routePath,
  toggleReminders,
}) => {
  const actions = useActions()

  const { hasGiftCardWallet, hasMembershipRewards } = useFlagsWithUseQueries()
  const { data: notificationCount } = useNotificationCount()
  const { data: pendingMembershipInvites } =
    useReceivedPendingMembershipInvites()

  const notificationsCounter =
    (notificationCount ?? 0) + (pendingMembershipInvites?.length ?? 0)

  const tabs: {
    onClick: () => void
    title: string
    icon: IconType
    routePath?: string
  }[] = [
    {
      onClick: () => actions.openAccount(),
      title: 'Home',
      icon: 'home',
      routePath: AccountRoute.path,
    },
    {
      onClick: () => actions.openPaymentSettings(),
      title: 'My Account',
      icon: 'user',
      routePath: PaymentSettingsRoute.path,
    },
    {
      onClick: () => actions.openCardHistory(),
      title: 'Card History',
      icon: 'card_history',
      routePath: CardHistoryRoute.path,
    },
    {
      onClick: () => actions.openDraftedOrders(),
      title: 'My Drafts',
      icon: 'drafts',
      routePath: DraftedOrdersRoute.path,
    },
    ...(hasGiftCardWallet
      ? [
          {
            onClick: () => actions.openMyGifts(),
            title: 'My Gift Cards',
            icon: 'gift' as IconType,
            routePath: MyGiftsRoute.path,
          },
        ]
      : []),
    {
      onClick: () => actions.openShareSettings(),
      title: 'Sharing',
      icon: 'share_settings',
      routePath: ShareSettingsRoute.path,
    },
    {
      onClick: () => actions.openDashboardSettingsRoute(),
      title: 'Settings',
      icon: 'settings',
      routePath: DashboardSettingsRoute.path,
    },
    {
      onClick: () => toggleReminders(),
      title: 'Notifications',
      icon: 'notifications',
    },
  ]
  const matchTabToRoutPath = tabs.findIndex(tab => tab.routePath === routePath)
  const defaultTabIndex = matchTabToRoutPath < 0 ? 0 : matchTabToRoutPath
  const account = useAccount()
  const membershipRewardsAmount = account.points?.amount ?? 0
  const window = useSelector(state => state.window)
  const shouldHideTabBarText = window.width <= 1245
  const isMobileNavBreakPoint = window.width >= 950
  const displayTabs = isMobileNavBreakPoint
    ? tabs.slice(0, tabs.length - 1)
    : tabs

  const isSmallMobile = useSelector(state => state.window.width < 520)

  return (
    <Flex css={styles.dashboardHeader}>
      <div css={styles.flexRowWrap}>
        <Flex
          columnGap="x4"
          inset={{ vertical: 'x1' }}
          justifyContent="center"
          style={
            isSmallMobile
              ? { height: '78px', overflow: 'visible' }
              : { overflow: 'visible' }
          }
        >
          {!isSmallMobile && (
            <TabBar
              tabs={displayTabs}
              activeTabIndex={defaultTabIndex}
              activeTabBackgroundColor="#BE9CDD"
              activeTabTextColor="#FFF"
              barBackgroundColor="#fff"
              barBoxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
              tabTextSize="13px"
              tabInsetVertical="x1"
              shouldHideText={shouldHideTabBarText}
            />
          )}
          {/* Notification Bubble */}
          {isMobileNavBreakPoint && (
            <Flex
              justifyContent="center"
              alignItems="center"
              backgroundColor="#f5f6f7"
              inset="x1"
              style={{ borderRadius: '100px' }}
            >
              <Div
                boxShadow={'mediumLight'}
                position="relative"
                inset={{ vertical: 'x1', horizontal: 'x1_5' }}
                borderRadius="100px"
                backgroundColor={'foreground'}
                display="flex"
                onClick={() => actions.openNotifications()}
                cursor={'pointer'}
                alignItems="center"
                flexDirection="row"
                columnGap="x_5"
              >
                {notificationsCounter && (
                  <Flex
                    position="absolute"
                    top="-10px"
                    right="-10px"
                    backgroundColor="#BE9CDD"
                    color="inverseHeading"
                    borderRadius={'100px'}
                    justifyContent="center"
                    alignItems="center"
                    style={{
                      padding: '1px 10px',
                      fontFamily: 'Montserrat, san serif',
                    }}
                  >
                    {notificationsCounter}
                  </Flex>
                )}
                <Div position="relative" top="3px">
                  <Icon name="notifications" size={22} />
                </Div>
              </Div>
            </Flex>
          )}
          {isMobileNavBreakPoint && hasMembershipRewards && (
            <MembershipRewards
              amount={membershipRewardsAmount}
              onClick={() => actions.openPaymentSettings()}
            />
          )}
        </Flex>
      </div>
      <Flex
        position="absolute"
        right={isSmallMobile ? '8px' : '24px'}
        onClick={onOpenAccount}
        cursor="pointer"
      >
        <ProfileAvatar
          account={account}
          size={40}
          isEditable={false}
          style={{
            color: 'white',
            fontSize: '1.1rem',
            letterSpacing: 1,
          }}
        />
      </Flex>
    </Flex>
  )
}

export default suspenseBoundary({
  component: DashboardHeader,
  unresolved: <Transition />,
  failure: error => `Failed loading: ${error}`,
})
